import React, { Component } from 'react';

const SEVERITY_COLOR = {
  critical: '#b91c1c',
  high: '#d97706',
  medium: '#2563eb',
  low: '#6b7280',
};

const SEVERITY_LABEL = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const SEVERITY_OPTIONS = ['critical', 'high', 'medium', 'low'];
const SCOPE_OPTIONS = ['structural', 'thematic'];
const SCOPE_LABEL = { structural: 'Structural', thematic: 'Thematic' };
const SCOPE_COLOR = { structural: '#3b82f6', thematic: '#ec4899' };

/**
 * KanbanIssueCard — issue card for the Kanban detail overlay.
 *
 * Props:
 *  issue     : object
 *  onSave?   : async ({ title, description, severity }) => void
 *  onDelete  : async () => void
 *  chapterOptions?: Array<{ id: string, chapter_number?: number, order_no?: number, title?: string }>
 *  onOpenChapterMoveModal?: () => void
 */
export default class KanbanIssueCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isDeleting: false,
      isSaving: false,
      saveError: null,
      draftTitle: props.issue?.title || '',
      draftDescription: props.issue?.description || '',
      draftSeverity: props.issue?.severity || 'medium',
      draftScope: props.issue?.scope || '',
    };
    this.saveTimer = null;
    this.lastSaved = {
      title: props.issue?.title || '',
      description: props.issue?.description || '',
      severity: props.issue?.severity || 'medium',
      scope: props.issue?.scope || '',
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.issue?.title !== this.props.issue?.title ||
      prevProps.issue?.description !== this.props.issue?.description ||
      prevProps.issue?.severity !== this.props.issue?.severity ||
      prevProps.issue?.scope !== this.props.issue?.scope
    ) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      this.setState({
        draftTitle: this.props.issue?.title || '',
        draftDescription: this.props.issue?.description || '',
        draftSeverity: this.props.issue?.severity || 'medium',
        draftScope: this.props.issue?.scope || '',
        saveError: null,
      });
      this.lastSaved = {
        title: this.props.issue?.title || '',
        description: this.props.issue?.description || '',
        severity: this.props.issue?.severity || 'medium',
        scope: this.props.issue?.scope || '',
      };
    }
  }

  componentWillUnmount() {
    this.flushPendingSaveSync();
  }

  flushPendingSaveSync = () => {
    clearTimeout(this.saveTimer);
    this.saveTimer = null;

    const normalizedTitle = String(this.state.draftTitle || '').trim();
    const normalizedDescription = String(this.state.draftDescription || '').trim();
    const normalizedSeverity = SEVERITY_OPTIONS.includes(String(this.state.draftSeverity || '').toLowerCase())
      ? String(this.state.draftSeverity).toLowerCase()
      : 'medium';
    const normalizedScope = SCOPE_OPTIONS.includes(String(this.state.draftScope || '').toLowerCase())
      ? String(this.state.draftScope).toLowerCase()
      : (this.state.draftScope || '');

    if (!normalizedTitle) {
      this.setState({ saveError: 'Title is required.' });
      return;
    }

    if (
      normalizedTitle === this.lastSaved.title &&
      normalizedDescription === this.lastSaved.description &&
      normalizedSeverity === this.lastSaved.severity &&
      normalizedScope === this.lastSaved.scope
    ) {
      return;
    }

    this.setState({ isSaving: true, saveError: null });
    Promise.resolve(this.props.onSave?.({
      title: normalizedTitle,
      description: normalizedDescription,
      severity: normalizedSeverity,
      scope: normalizedScope,
    }))
      .then(() => {
        this.lastSaved = {
          title: normalizedTitle,
          description: normalizedDescription,
          severity: normalizedSeverity,
          scope: normalizedScope,
        };
      })
      .catch(() => {
        this.setState({ saveError: 'Failed to save changes.' });
      })
      .finally(() => {
        this.setState({ isSaving: false });
      });
  };

  handleDelete = async () => {
    this.setState({ isDeleting: true });
    try {
      await this.props.onDelete();
    } finally {
      this.setState({ isDeleting: false });
    }
  };

  handleTitleChange = (e) => {
    const draftTitle = e.target.value;
    this.setState({ draftTitle, saveError: null });
  };

  handleDescriptionChange = (e) => {
    const draftDescription = e.target.value;
    this.setState({ draftDescription, saveError: null });
  };

  handleSeverityChange = (e) => {
    const draftSeverity = e.target.value;
    this.setState({ draftSeverity, saveError: null });
  };

  handleScopeChange = (e) => {
    const draftScope = e.target.value;
    this.setState({ draftScope, saveError: null }, () => {
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(this.flushPendingSaveSync, 300);
    });
  };

  render() {
    const { issue, chapterOptions = [], onOpenChapterMoveModal } = this.props;
    const { isDeleting, isSaving, saveError, draftTitle, draftDescription, draftSeverity, draftScope } = this.state;

    const color = SEVERITY_COLOR[draftSeverity] || SEVERITY_COLOR.medium;
    const savedTitle = String(this.lastSaved.title || '').trim();
    const savedDescription = String(this.lastSaved.description || '').trim();
    const savedSeverity = String(this.lastSaved.severity || 'medium').toLowerCase();
    const normalizedDraftTitle = String(draftTitle || '').trim();
    const normalizedDraftDescription = String(draftDescription || '').trim();
    const normalizedDraftSeverity = String(draftSeverity || 'medium').toLowerCase();
    const savedScope = String(this.lastSaved.scope || '').toLowerCase();
    const normalizedDraftScope = String(draftScope || '').toLowerCase();
    const isTitleDirty = normalizedDraftTitle !== savedTitle;
    const isDescriptionDirty = normalizedDraftDescription !== savedDescription;
    const isSeverityDirty = normalizedDraftSeverity !== savedSeverity;
    const isScopeDirty = issue.scope != null && normalizedDraftScope !== savedScope;
    const hasPendingChanges = isTitleDirty || isDescriptionDirty || isSeverityDirty || isScopeDirty;
    const saveStatusText = isSaving
      ? (isTitleDirty && isDescriptionDirty && isSeverityDirty
        ? 'Saving title, description and severity…'
        : isTitleDirty && isDescriptionDirty
          ? 'Saving title and description…'
          : isTitleDirty && isSeverityDirty
            ? 'Saving title and severity…'
            : isDescriptionDirty && isSeverityDirty
              ? 'Saving description and severity…'
              : isTitleDirty
                ? 'Saving title…'
                : isDescriptionDirty
                  ? 'Saving description…'
                  : isSeverityDirty
                    ? 'Saving severity…'
                    : 'Saving…')
      : hasPendingChanges
        ? 'Changes pending…'
        : 'Saved';

    const isProjectLevel = !issue.chapter_id || issue.chapter_number === 0 || issue.chapter_number === null;
    const scopeLabel = isProjectLevel ? 'Project' : `Ch ${issue.chapter_number}`;
    const canReassignInHeader = Array.isArray(chapterOptions) && chapterOptions.length > 0 && typeof onOpenChapterMoveModal === 'function';
    const selectedChapterId = issue.chapter_id || '';
    const chapterUrl = issue.chapter_id
      ? `/project/${issue.project_id}/chapter/${issue.chapter_id}#tasks`
      : null;

    return (
      <div className="issue-card">
        <div className="issue-card-header" style={{ cursor: 'default', userSelect: 'text' }}>
          <span className="issue-card-severity-dot" style={{ background: color }} aria-hidden="true" />
          {issue.scope != null ? (
            <select
              className={`issue-card-severity-select issue-card-severity-select--${draftSeverity}`}
              value={draftSeverity}
              onChange={this.handleSeverityChange}
              onBlur={this.flushPendingSaveSync}
              aria-label="Issue severity"
            >
              {SEVERITY_OPTIONS.map((severity) => (
                <option key={severity} value={severity}>
                  {SEVERITY_LABEL[severity]}
                </option>
              ))}
            </select>
          ) : (
            <select
              className={`issue-card-severity-select issue-card-severity-select--${draftSeverity}`}
              value={draftSeverity}
              onChange={this.handleSeverityChange}
              onBlur={this.flushPendingSaveSync}
              aria-label="Issue severity"
            >
              {SEVERITY_OPTIONS.map((severity) => (
                <option key={severity} value={severity}>
                  {SEVERITY_LABEL[severity]}
                </option>
              ))}
            </select>
          )}
          {canReassignInHeader ? (
            <select
              className="issue-card-severity-select"
              value={selectedChapterId}
              onMouseDown={(e) => {
                e.preventDefault();
                onOpenChapterMoveModal();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  onOpenChapterMoveModal();
                }
              }}
              onChange={() => {}}
              aria-label="Reassign chapter"
              title="Open chapter list"
            >
              {chapterOptions.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  Ch. {ch.chapter_number ?? ch.order_no} — {ch.title}
                </option>
              ))}
            </select>
          ) : (
            <span className="issue-card-scope">{scopeLabel}</span>
          )}
        </div>
        <div className="issue-card-body">
          {issue.scope != null && (
            <>
              <label className="issue-card-label">Scope</label>
              <select
                className="issue-card-select"
                value={draftScope}
                onChange={this.handleScopeChange}
                aria-label="Issue scope"
              >
                {SCOPE_OPTIONS.map((s) => (
                  <option key={s} value={s}>{SCOPE_LABEL[s]}</option>
                ))}
              </select>
              {isSaving && isScopeDirty && <span className="issue-card-field-saving">Saving scope…</span>}
            </>
          )}
          <label className="issue-card-label">Title</label>
          <input
            type="text"
            className="issue-card-input"
            value={draftTitle}
            onChange={this.handleTitleChange}
            onBlur={this.flushPendingSaveSync}
          />
          {isSaving && isTitleDirty && <span className="issue-card-field-saving">Saving title…</span>}
          {!isSaving && isTitleDirty && <span className="issue-card-field-dirty">Title changed…</span>}
          <label className="issue-card-label">Description</label>
          <textarea
            className="issue-card-textarea"
            rows={6}
            value={draftDescription}
            onChange={this.handleDescriptionChange}
            onBlur={this.flushPendingSaveSync}
          />
          {isSaving && isDescriptionDirty && <span className="issue-card-field-saving">Saving description…</span>}
          {!isSaving && isDescriptionDirty && <span className="issue-card-field-dirty">Description changed…</span>}
          {issue.suggestion && (
            <div className="issue-card-suggestion">
              <strong>Suggestion:</strong> {issue.suggestion}
            </div>
          )}
          {issue.issue_type && (
            <p className="issue-card-type">Type: {issue.issue_type.replace(/_/g, ' ')}</p>
          )}
          {saveError && <p className="issue-card-save-error">{saveError}</p>}
          <div className="issue-card-actions">
            <span className="issue-card-saving-badge">{saveStatusText}</span>
            {chapterUrl && (
              <a href={chapterUrl} className="issue-card-btn issue-card-btn--flow">
                ▶ Solve This
              </a>
            )}
            <button
              type="button"
              className="issue-card-btn issue-card-btn--delete"
              onClick={this.handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
